import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { CategoryNode, WorkItemCard } from '../lib/types';

/**
 * Picking what a task is about. The list is flattened to "Parent › Child" so one select does
 * the job of two, and a category already on the task stays selectable even once retired —
 * otherwise editing anything else on an old task would silently drop its category.
 */
export function CategoryField({
  item,
  companyId,
  editable,
  onChanged,
}: {
  item: WorkItemCard;
  companyId: string;
  editable: boolean;
  onChanged: () => void | Promise<void>;
}) {
  const [options, setOptions] = useState<Array<{ id: string; path: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<CategoryNode[]>(`/companies/${companyId}/categories`)
      .then((tree) => {
        const flat: Array<{ id: string; path: string }> = [];
        for (const parent of tree) {
          flat.push({ id: parent.id, path: parent.name });
          for (const child of parent.children ?? []) flat.push({ id: child.id, path: `${parent.name} › ${child.name}` });
        }
        setOptions(flat);
      })
      .catch(() => setOptions([]));
  }, [companyId]);

  async function choose(categoryId: string) {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/work-items/${item.id}`, categoryId ? { categoryId } : { clearCategory: true });
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not set that category');
    } finally {
      setBusy(false);
    }
  }

  const known = options.some((o) => o.id === item.category?.id);

  /**
   * Not a disabled dropdown — the value, plainly.
   *
   * A greyed select still reads as a control: it invites a click, and a customer who cannot
   * file work under a category should not be shown the list of ours to browse through. What
   * they want from this field is to know what it says.
   */
  if (!editable) {
    return (
      <div className="field">
        <span className="field-label">Category — what it is about</span>
        <span>{item.category?.path ?? <span className="dim">Uncategorised</span>}</span>
      </div>
    );
  }

  return (
    <label className="field">
      <span className="field-label">Category — what it is about</span>
      <select value={item.category?.id ?? ''} disabled={busy} onChange={(e) => choose(e.target.value)}>
        <option value="">Uncategorised</option>
        {/* A retired category already on the task must stay listed, or saving would drop it. */}
        {item.category && !known && <option value={item.category.id}>{item.category.path} (retired)</option>}
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.path}
          </option>
        ))}
      </select>
      {options.length === 0 && (
        <span className="field-hint">No categories set up for this company yet.</span>
      )}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}
